import { ArrowUpRight, Target, AlertCircle, Layers, Sparkles, TrendingUp, Lightbulb, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import AINetwork from '@/components/AINetwork';
import CTABand from '@/components/CTABand';
import { CASE_STUDIES } from '@/lib/caseStudies';

const accentMap = {
  cyan: { ring: 'border-cyan-400/30', glow: 'hover:shadow-[0_0_40px_rgba(34,211,238,0.15)]', tag: 'text-cyan-300', tagBg: 'bg-cyan-400/5 border-cyan-400/20', dot: 'bg-cyan-400' },
  purple: { ring: 'border-purple-400/30', glow: 'hover:shadow-[0_0_40px_rgba(168,85,247,0.15)]', tag: 'text-purple-300', tagBg: 'bg-purple-400/5 border-purple-400/20', dot: 'bg-purple-400' },
};

export default function CaseStudiesPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <AINetwork density={28} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-5">Case Studies / Portfolio</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter max-w-5xl text-balance">
            Strategy in motion. <span className="gradient-text">Outcomes on record.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-white/70 leading-relaxed">
            Real engagements across astrology, wellness and healing, and Vastu consulting — where
            authentic positioning, AI-led strategy and full-funnel design took trusted practices to
            global audiences and grew both their reach and revenue. As a practitioner of astrology
            and Vastu myself, I speak this industry's language.
          </p>

          {/* In-page anchor index */}
          <div className="mt-10 flex flex-wrap gap-2">
            {CASE_STUDIES.map((cs, i) => (
              <a
                key={cs.slug}
                href={`#${cs.slug}`}
                data-testid={`case-anchor-${cs.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors text-xs"
              >
                <span className="font-mono-display text-cyan-300/70">0{i + 1}</span>
                {cs.industry}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section data-testid="section-case-studies" className="relative border-t border-white/5">
        {CASE_STUDIES.map((cs, idx) => {
          const Icon = cs.icon;
          const a = accentMap[cs.accent];
          return (
            <article
              key={cs.slug}
              id={cs.slug}
              data-testid={`case-study-${cs.slug}`}
              className="relative py-20 lg:py-28 border-b border-white/5 scroll-mt-28"
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                {/* Header row */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
                  <div className="lg:col-span-8">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${a.tagBg} text-[10px] uppercase tracking-[0.22em] ${a.tag} mb-5`}>
                      <span className="font-mono-display">Case Study 0{idx + 1}</span>
                      <span className="opacity-50">·</span>
                      <span>{cs.industry}</span>
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight tracking-tight text-balance mb-4">
                      {cs.client}
                    </h2>
                    <p className="text-white/60 text-sm sm:text-base">{cs.location}</p>
                  </div>
                  <div className="lg:col-span-4 flex lg:justify-end">
                    <div className={`w-16 h-16 rounded-2xl border ${a.ring} bg-white/[0.02] grid place-items-center`}>
                      <Icon className={`w-7 h-7 ${a.tag}`} strokeWidth={1.4} />
                    </div>
                  </div>
                </div>

                {/* Objective */}
                <div className={`rounded-2xl border ${a.ring} bg-[#0B1221]/40 p-7 mb-10`}>
                  <div className="flex items-start gap-4">
                    <Target className={`w-5 h-5 mt-1 ${a.tag} shrink-0`} strokeWidth={1.6} />
                    <div>
                      <div className="text-xs uppercase tracking-[0.22em] text-brand-ink mb-2">Objective</div>
                      <p className="font-heading text-xl sm:text-2xl text-white/90 leading-snug text-balance">{cs.objective}</p>
                    </div>
                  </div>
                </div>

                {/* Practitioner Note */}
                {cs.practitionerNote && (
                  <div className="rounded-2xl border border-purple-400/20 bg-purple-400/[0.04] p-7 mb-10">
                    <div className="flex items-start gap-4">
                      <Quote className="w-5 h-5 mt-1 text-purple-300 shrink-0" strokeWidth={1.6} />
                      <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-purple-300 mb-2">From the Founder</div>
                        <p className="text-sm sm:text-base text-white/80 leading-relaxed italic">{cs.practitionerNote}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Challenge / Strategy / AI grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
                  <Block icon={AlertCircle} title="Challenge" accent={a} items={cs.challenges} />
                  <Block icon={Layers} title="Strategy Implemented" accent={a} items={cs.strategy} />
                  <Block icon={Sparkles} title="AI Integration" accent={a} items={cs.aiUse} />
                </div>

                {/* Results */}
                {cs.results && cs.results.length > 0 && (
                  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B1221] to-[#11071f] p-8 mb-8">
                    <div className="flex items-center gap-3 mb-6">
                      <TrendingUp className={`w-5 h-5 ${a.tag}`} strokeWidth={1.6} />
                      <div className="text-xs uppercase tracking-[0.22em] text-brand-ink">Results</div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                      {cs.results.map((r, i) => (
                        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                          <div className="font-heading text-3xl sm:text-4xl font-semibold gradient-text-static">{r.k}</div>
                          <div className="mt-2 text-xs text-white/70 leading-snug">{r.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Learning */}
                {cs.learning && (
                  <div className="rounded-2xl border-l-2 border-cyan-400 bg-cyan-400/[0.04] p-7">
                    <div className="flex items-start gap-4">
                      <Lightbulb className="w-5 h-5 mt-1 text-cyan-300 shrink-0" strokeWidth={1.6} />
                      <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-cyan-300 mb-2">Key Learning</div>
                        <p className="font-heading text-lg sm:text-xl text-white/90 leading-snug text-balance">
                          &ldquo;{cs.learning}&rdquo;
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {/* Closing strip */}
      <section className="relative py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-3">More mandates</div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold leading-snug max-w-2xl">
                Additional engagements across occult, spiritual and holistic wellness practices available on request.
              </h3>
            </div>
            <Link
              to="/contact"
              data-testid="case-studies-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors text-sm shrink-0"
            >
              Request full portfolio <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABand title="Your industry. Your growth chapter. Next." ctaLabel="Discuss Your Growth Goals" />
    </div>
  );
}

function Block({ icon: Icon, title, accent, items }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0B1221]/40 p-6 hover:border-cyan-400/20 transition-colors h-full">
      <div className="flex items-center gap-3 mb-5">
        <Icon className={`w-5 h-5 ${accent.tag}`} strokeWidth={1.6} />
        <div className="text-xs uppercase tracking-[0.22em] text-brand-ink">{title}</div>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-white/75 leading-relaxed">
            <span className={`mt-2 w-1 h-1 rounded-full ${accent.dot} shrink-0`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
